// Assists the client applies by itself: pause-on-danger (settings store),
// the speed cap (TimeControls) and hints (coachmarks). The sim owns the
// rest — handicap and reveal map fold into spawn, auto-manage runs (or
// offers `suggestIntents` buttons) — so nothing here fakes those; UI mode
// is consumed by the cinematic director, not this module.

import type { Assists } from '@aod/protocol';

export const SPEED_OPTIONS = [1, 2, 4] as const;
export type SpeedOption = (typeof SPEED_OPTIONS)[number];

/** Speeds the time controls offer under `cap` (always at least 1×). */
export function allowedSpeeds(cap = 4): SpeedOption[] {
  const out = SPEED_OPTIONS.filter((s) => s <= cap);
  return out.length > 0 ? out : [1];
}

/** Clamp a requested speed to the cap. */
export function clampSpeed(speed: number, cap = 4): number {
  return Math.max(1, Math.min(speed, cap));
}

export interface HintPolicy {
  /** Let the coachmark sequence open hints on its own during the match. */
  readonly autoOpen: boolean;
  /** Forget which hints were seen, so they show again this match. */
  readonly resetSeenOnStart: boolean;
}

/** off: no automatic hints (Help still works) · smart: each once · always: every match. */
export function hintPolicy(hints: Assists['hints'] | undefined): HintPolicy {
  switch (hints) {
    case 'off':
      return { autoOpen: false, resetSeenOnStart: false };
    case 'always':
      return { autoOpen: true, resetSeenOnStart: true };
    default:
      return { autoOpen: true, resetSeenOnStart: false };
  }
}

export interface MatchStartEffects {
  readonly setPauseOnDanger: (mode: Assists['pauseOnDanger']) => void;
  readonly resetCoachmarks: () => void;
}

/** Apply the human seat's client-side assists when a single-player match starts. */
export function applyAssistsAtMatchStart(assists: Assists, fx: MatchStartEffects): void {
  fx.setPauseOnDanger(assists.pauseOnDanger);
  if (hintPolicy(assists.hints).resetSeenOnStart) fx.resetCoachmarks();
}
